import apiClient from '@shared/api/apiClient';

/**
 * 남은 휴가 조회 API
 * - withAuth: true → 자동으로 accessToken/enumber/companyCode 추가
 * @returns {Promise<Object>}
 */
async function getRemainVacation() {
  const response = await apiClient.get('/v1/vacations/remain', {
    withAuth: true,
  });
  return response.data;
}

/**
 * 특정 휴가 문서 상세 조회 API
 *  - withAuth: true → 자동으로 accessToken/enumber/companyCode 추가
 * @param {number|string} idx - 휴가 문서 ID
 */
async function getVacationDetail(idx) {
  const response = await apiClient.get(`/v1/vacations/${idx}`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 특정 연도의 휴가 신청 내역 조회 API
 * - withAuth: true → Axios 인터셉터에서 자동으로 인증 헤더 추가
 * @param {number|string} year
 */
async function getVacationList(year) {
  const response = await apiClient.get(`/v1/vacations/list/${year}`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 휴가 종류 리스트 조회 API
 *  - withAuth: true → 자동으로 accessToken/enumber/companyCode 추가
 */
async function getVacationTypes() {
  const response = await apiClient.get('/v1/vacations/types', { withAuth: true });
  return response.data;
}

/**
 * 휴가 신청 API 요청 함수
 * - 휴가 신청 데이터(기간, 사유, 종류 등)를 서버로 전달하여 신청 또는 수정 처리 수행
 * - 성공 시 서버에서 반환한 휴가 처리 결과 데이터를 반환함
 *  - withAuth: true → 자동으로 accessToken/enumber/companyCode 추가
 * @param {Object} params - 휴가 신청에 필요한 파라미터
 * @param {number} params.idx - 휴가 문서 idx (신규 0)
 * @param {number} params.enumber - 신청자 사번
 * @param {number} params.company - 회사 코드
 * @param {string} params.date - 문서 작성일자 (YYYYMMDD)
 * @param {string} params.vacation_type - 휴가 종류 (연차, 반차 등)
 * @param {number} params.state - 문서 상태 (0: 임시, 1: 제출 등)
 * @param {string} params.start_date - 휴가 시작일 (YYYYMMDD)
 * @param {string} params.end_date - 휴가 종료일 (YYYYMMDD)
 * @param {number} params.period - 휴가 기간
 * @param {string} params.reason - 휴가 사유
 * @param {number} params.signer_enumber - 승인자 사번
 * @returns {Promise<Object>} 서버 응답 데이터
 */
async function saveVacation(params) {
  const response = await apiClient.put('/v1/vacations', params, { withAuth: true });

  const data = response.data;

  if (data?.error_msg) {
    throw new Error(data.error_msg);
  }

  return response.data;
}

/** 휴가 결재 리스트 조회
 * @param {string} startDate - 조회 시작일 (YYYYMMDD)
 * @param {string} endDate - 조회 종료일 (YYYYMMDD)
 * @param {number} stateCode - 상태 코드
 * @param {number} vacationType - 휴가 구분 코드
 * @param {number} teamCode - 기안 부서
 * @param {number} name - 기안자
 * @returns {Promise<Object>} resultData 객체
 */
async function getVacationProposeList({
  startDate,
  endDate,
  stateCode,
  vacationType,
  teamCode,
  name,
}) {
  const response = await apiClient.get('/v1/vacations/propose/list', {
    params: {
      startDate,
      endDate,
      stateCode,
      vacationType,
      teamCode,
      name,
    },
    withAuth: true,
  });

  return response.data;
}

/**
 * 휴가 결재 목록 조회 (POST)
 * @param {Object} body - {
 *   startDate, endDate, stateCode, teamCode, name, vacationType
 * }
 */
async function postVacationProposeList(body) {
  const response = await apiClient.post('/v1/vacations/propose/list-post', body, {
    withAuth: true,
  });

  return response.data;
}

/**
 * 특정 기간 내의 휴일 조회 get api
 * @param {string} startDate - 조회 시작일 (YYYYMMDD)
 * @param {string} endDate - 조회 종료일 (YYYYMMDD)
 */
async function getHolidayList({ startDate, endDate }) {
  const response = await apiClient.get('/v1/vacations/holiday', {
    params: {
      startDate,
      endDate,
    },
    withAuth: true,
  });

  return response.data;
}

/**
 * 특정 기간 내 휴일 조회 (post)
 * @param {Object} body - {
 *   startDate, endDate
 * }
 */
async function postHolidayList(body) {
  const response = await apiClient.post('/v1/vacations/holiday-post', body, {
    withAuth: true,
  });

  return response.data;
}
/**
 * 잔여 휴가 기간 조회(특정사원)
 * @param {number} enumber - 잔여 휴가를 조회할 사원번호
 */
async function getRemainVacationByEnumber(enumber) {
  const response = await apiClient.get(`/v1/vacations/remain/${enumber}`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 휴가 결재 처리 API
 * - @param {number} idx: 결재 문서 ID
 * - @param {string} comment: 승인 코멘트
 */
async function putVacationApprovalDone(idx, comment) {
  const response = await apiClient.put(
    '/v1/vacations/approval/done',
    {
      idx,
      comment,
    },
    {
      withAuth: true,
    },
  );
  return response.data;
}

/**
 * 휴가 반려 처리 API
 * - @param {number} idx: 결재 문서 ID
 * - @param {string} comment: 승인 코멘트
 */
async function putVacationApprovalReturn(idx, comment) {
  const response = await apiClient.put(
    '/v1/vacations/approval/return',
    {
      idx,
      comment,
    },
    {
      withAuth: true,
    },
  );
  return response.data;
}

/**
 * 휴가 검색 필터 조회
 */
async function getVacationSearchFilter() {
  const response = await apiClient.get('/v1/vacations/search/filters', {
    withAuth: true,
  });
  return response.data;
}

/**
 * 휴가 경조 타입 조회
 * @param {number} eventTypeCode : 휴가 타입 코드
 */
async function getVacationEventType(eventTypeCode) {
  const response = await apiClient.get(`/v1/vacations/event/types/${eventTypeCode}`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 휴가함 카운터 정보 요청
 */
async function getVacationCountInfo() {
  const response = await apiClient.get('/v1/vacations/count', {
    withAuth: true,
  });
  return response.data;
}

/**
 * 로그인한 사람의 상위 결재자 체크
 */
async function getVacationCheckApprover() {
  const response = await apiClient.get('/v1/vacations/check-approver', {
    withAuth: true,
  });
  return response.data;
}

/**
 * 특정 회사의 전체 휴가 리스트 조회 (POST)
 * @param {string} name - 직원명
 * @param {string} startDate - 조회 시작일 YYYY-MM-DD
 * @param {string} endDate - 조회 종료일 YYYY-MM-DD
 * @param {number} stateCode - 상태 코드
 * @param {number} teamCode - 팀 코드
 * @param {number} vacationType - 휴가 타입 코드
 */
async function postVacationAllList({
  name,
  startDate,
  endDate,
  stateCode,
  teamCode,
  vacationType,
}) {
  const response = await apiClient.post(
    '/v1/vacations/list/all',
    {
      name,
      startDate,
      endDate,
      stateCode,
      teamCode,
      vacationType,
    },
    {
      withAuth: true,
    },
  );

  return response.data;
}
/**
 * 휴가 삭제
 * @param {number} idx : 휴가 고유 번호
 */
async function getVacationDeleteByIdx(idx) {
  const response = await apiClient.get(`/v1/vacations/${idx}/delete`, {
    withAuth: true,
  });
  return response.data;
}

/**
 * 휴가 첨부파일 등록
 * @param {FormData} formData - 첨부파일 FormData
 * @param {number} vacationIdx - 휴가 idx
 */
async function saveVacationFile(formData, vacationIdx) {
  const response = await apiClient.post(`/v1/vacations/files/${vacationIdx}`, formData, {
    withAuth: true,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * 휴가 첨부파일 다운로드 URL 조회 API
 * (Webview 등에서 Blob 에러 방지를 위해 실제 물리 파일 경로를 반환)
 * @param {number} file_idx : 파일 고유 번호
 */
async function getVacationFile(file_idx) {
  const response = await apiClient.get(`/v1/vacations/files/${file_idx}/download-url`, {
    withAuth: true,
  });

  return response.data;
}

/**
 * 휴가 관리자 삭제 API
 * - 관리자 권한으로 휴가 상태를 삭제(상태 7로 변경)
 * @param {number} idx - 휴가 문서 고유 번호
 * @returns {Promise<Object>} 서버 응답 데이터
 */
async function deleteAdminVacationByIdx(idx) {
  if (!idx) {
    throw new Error('deleteAdminVacationByIdx: idx는 필수 값입니다.');
  }

  const response = await apiClient.delete(`/v1/vacations/${idx}`, {
    withAuth: true,
  });

  return response.data;
}
export {
  getRemainVacation,
  getVacationDetail,
  getVacationList,
  getVacationTypes,
  saveVacation,
  getVacationProposeList,
  postVacationProposeList,
  getHolidayList,
  postHolidayList,
  getRemainVacationByEnumber,
  putVacationApprovalDone,
  putVacationApprovalReturn,
  getVacationSearchFilter,
  getVacationEventType,
  getVacationCountInfo,
  getVacationCheckApprover,
  postVacationAllList,
  getVacationDeleteByIdx,
  saveVacationFile,
  getVacationFile,
  deleteAdminVacationByIdx,
};
